import { Avatar, AvatarFallback, AvatarImage } from '@/components/UI/Avatar';
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger
} from '@/components/UI/Dropdown';
import { Input } from '@/components/UI/Input';
import { Building2, Check, ListFilter } from 'lucide-react';
import { useMemo, useState, type FC } from 'react';

import useUserCompanies from '../../../util/hooks/queries/useUserCompanies';
import { useSwitchCompany } from '../hooks/useSwitchCompany';

interface OrganisationSwitcherProps {
  /** The active company's registered name (matches the menu's identity row). */
  currentName?: string | null;
  /** The active company's logo, if any. */
  profileImageUrl?: string;
}

/** Display label for a company — falls back to the id when the name isn't set yet. */
const companyLabel = (registeredName: string | null, companyId: number): string =>
  registeredName ?? `Company ${companyId}`;

/**
 * The account-menu identity row. With a single company it's a plain label; with several it becomes
 * a submenu whose trigger shows the current organisation and whose popout (filtered, scrollable)
 * lets the user switch. The popout opens to the side — left in practice, since the account menu is
 * anchored top-right and Radix flips on collision.
 */
export const OrganisationSwitcher: FC<OrganisationSwitcherProps> = ({
  currentName,
  profileImageUrl
}) => {
  const { data: companies, isLoading } = useUserCompanies();
  const { mutate: switchCompany, isPending: isSwitching } = useSwitchCompany();
  const [query, setQuery] = useState('');

  const activeLabel =
    currentName?.trim() ||
    (() => {
      const current = companies?.find((c) => c.isCurrentDefault);
      return current ? companyLabel(current.registeredName, current.companyId) : '';
    })();

  const filtered = useMemo(() => {
    if (!companies) return [];
    const q = query.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((company) =>
      [
        companyLabel(company.registeredName, company.companyId),
        company.registrationNumber ?? ''
      ]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [companies, query]);

  const avatar = (
    <Avatar className="bg-background size-7 rounded">
      <AvatarImage
        src={profileImageUrl}
        alt="Company logo"
        className="border object-cover"
      />
      <AvatarFallback className="size-7 rounded text-xs uppercase">
        {activeLabel ? activeLabel.charAt(0) : 'C'}
      </AvatarFallback>
    </Avatar>
  );

  // Single-company (or still loading) users have nothing to switch to — show a plain identity row.
  if (isLoading || !companies || companies.length <= 1) {
    return (
      <DropdownMenuLabel className="flex items-center gap-2.5 px-4 py-2.5">
        {avatar}
        <span className="truncate text-[13px] font-medium text-gray-900">
          {activeLabel}
        </span>
      </DropdownMenuLabel>
    );
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="gap-2.5 px-4 py-2.5 [&>svg]:text-gray-400">
        {avatar}
        <span className="truncate text-[13px] font-medium text-gray-900">
          {activeLabel}
        </span>
      </DropdownMenuSubTrigger>

      <DropdownMenuPortal>
        <DropdownMenuSubContent alignOffset={-4} sideOffset={4} className="w-64 p-0">
          <p className="px-4 pt-2.5 pb-1.5 text-[11px] font-medium tracking-wider text-gray-400 uppercase">
            Switch organisation
          </p>

          {/* Plain wrapper (not a menu item) so clicks don't select/close. Stop key events from
              bubbling to Radix, otherwise typing triggers the menu's typeahead / arrow navigation. */}
          <div className="px-3 pb-2" onKeyDown={(e) => e.stopPropagation()}>
            <Input
              size="sm"
              icon={<ListFilter className="h-3.5 w-3.5 text-gray-400" />}
              placeholder="Filter…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Filter organisations"
            />
          </div>

          <div className="max-h-64 overflow-y-auto pb-1">
            {filtered.length === 0 ? (
              <p className="px-4 py-3 text-[13px] text-gray-400">
                No organisations found.
              </p>
            ) : (
              filtered.map((company) => (
                <DropdownMenuItem
                  key={company.companyId}
                  disabled={company.isCurrentDefault || isSwitching}
                  onClick={() => {
                    if (!company.isCurrentDefault) {
                      switchCompany({ companyId: company.companyId });
                    }
                  }}
                  className="gap-2.5 px-4 py-2 text-[13px] font-normal text-gray-700"
                >
                  <Building2 className="h-4 w-4 shrink-0 text-gray-400" />
                  <span className="flex-1 truncate">
                    {companyLabel(company.registeredName, company.companyId)}
                  </span>
                  {company.isCurrentDefault && (
                    <Check className="h-3.5 w-3.5 shrink-0 text-green-500" />
                  )}
                </DropdownMenuItem>
              ))
            )}
          </div>
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
};
